import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Drawer, IconButton, Link, Typography, useTheme } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { PreviewDrawerProps } from './PreviewDrawer.props';
import { PreviewDrawerActions } from './PreviewDrawerActions';
import { PreviewDrawerNotes } from './PreviewDrawerNotes';
import { PreviewDrawerSLA } from './PreviewDrawerSLA';
import { PreviewDrawerSummary } from './PreviewDrawerSummary';
import { useGetServiceLogsByServiceId } from '../../../queries/service-log-query';
import { useGetServiceById } from '../../../queries/service-query';

const DRAWER_WIDTH = 400;

export const PreviewDrawer: React.FC<PreviewDrawerProps> = ({
  serviceId,
  open,
  onClose,
  service: serviceProp,
  logs: logsProp,
  onAction,
}) => {
  const theme = useTheme();

  // Fetch data when opened — use props if provided, otherwise fetch
  const serviceQuery = useGetServiceById(serviceId ?? 0);
  const logsQuery = useGetServiceLogsByServiceId(serviceId ?? 0);

  const service = serviceProp ?? (open ? serviceQuery.data ?? null : null);
  const serviceRecord = service ? (service as unknown as Record<string, unknown>) : null;
  const logs = logsProp ?? (open ? (logsQuery.data as unknown as Array<Record<string, unknown>>) ?? undefined : undefined);
  const isLoading = !serviceProp && open && serviceQuery.isLoading;

  const handleAction = useCallback(
    (type: string) => {
      onAction?.(type);
    },
    [onAction]
  );

  const jobId = serviceRecord ? String(serviceRecord.serviceId ?? serviceRecord.id ?? '') : undefined;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      data-testid="preview-drawer"
      slotProps={{
        paper: {
          sx: {
            width: DRAWER_WIDTH,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme.palette.v4.surface,
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.v4.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {jobId ?? `#${serviceId ?? ''}`}
          </Typography>
          <Link
            component={RouterLink}
            to={serviceId ? `/services/${serviceId}` : '#'}
            underline="hover"
            aria-label="Open job details"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: theme.palette.v4.accent,
              fontSize: '0.8125rem',
            }}
          >
            Open
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close preview drawer"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Body — scrollable */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <PreviewDrawerSummary service={serviceRecord} />
            <PreviewDrawerSLA service={serviceRecord} />
            <PreviewDrawerNotes logs={logs} />
          </>
        )}
      </Box>

      {/* Footer */}
      <PreviewDrawerActions onAction={handleAction} />
    </Drawer>
  );
};
